import React from 'react';
import { motion } from 'motion/react';
import { ArrowRight, Building2, Gem, Home, Map, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

const services = [
  {
    title: 'Private Residences',
    description: 'Large-format homes planned around light, privacy, and long-term family use.',
    icon: Home,
    tone: 'dark',
  },
  {
    title: 'Commercial Anchors',
    description: 'Workspaces and mixed-use addresses with a more composed front-of-house experience.',
    icon: Building2,
  },
  {
    title: 'Master Planning',
    description: 'Street rhythm, land efficiency, and phased growth strategies that remain elegant.',
    icon: Map,
  },
  {
    title: 'Quality Oversight',
    description: 'Material review, construction discipline, and delivery standards that stay measurable.',
    icon: ShieldCheck,
  },
  {
    title: 'Interior Direction',
    description: 'Palettes, finishes, and detailing that feel quiet, tactile, and costly in the right way.',
    icon: Gem,
  },
  {
    title: 'Smart Infrastructure',
    description: 'Discreet technology layers built into timeless envelopes rather than added after.',
    icon: Sparkles,
  },
];

export default function Services() {
  return (
    <section className="pb-28 pt-6 md:pb-32">
      <div className="section-shell">
        <div className="soft-panel overflow-hidden p-8 md:p-10 lg:p-12">
          <div className="grid gap-10 lg:grid-cols-[320px_minmax(0,1fr)] lg:gap-12">
            <div className="lg:sticky lg:top-28">
              <span className="editorial-kicker">How we work</span>
              <h2 className="mt-7 text-balance text-4xl leading-tight sm:text-5xl md:text-6xl">
                Every project begins with land, circulation, and <span className="gold-text-gradient">how calm feels</span> in the room.
              </h2>
              <p className="mt-6 text-base leading-8 text-secondary/65 md:text-lg">
                The studio combines architectural planning, construction oversight, and hospitality thinking.
                That mix is what lets the finished spaces feel composed instead of merely expensive.
              </p>
              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/6 px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-secondary transition-all duration-300 hover:border-accent/40 hover:text-accent"
              >
                Request a Proposal
                <ArrowRight size={16} />
              </Link>
            </div>

            <div>
              <div className="grid gap-5 md:grid-cols-2">
                {services.map((service, index) => (
                  <motion.div
                    key={service.title}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.55, delay: index * 0.05 }}
                    className={service.tone === 'dark' ? 'dark-panel p-7 md:col-span-2' : 'rounded-[1.75rem] border border-white/10 bg-white/5 p-7 shadow-[0_18px_50px_rgba(0,0,0,0.4)]'}
                  >
                    <div className={service.tone === 'dark' ? 'mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-gold-light' : 'mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-accent/10 text-accent'}>
                      <service.icon size={24} />
                    </div>
                    <h3 className={service.tone === 'dark' ? 'text-3xl text-secondary' : 'text-2xl text-secondary'}>
                      {service.title}
                    </h3>
                    <p className={service.tone === 'dark' ? 'mt-3 max-w-2xl text-secondary/70 leading-7' : 'mt-3 leading-7 text-secondary/65'}>
                      {service.description}
                    </p>
                  </motion.div>
                ))}
              </div>

              <div className="dark-panel mt-6 grid gap-6 p-7 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light/85">
                    Studio standard
                  </p>
                  <p className="mt-3 max-w-2xl text-2xl font-serif text-secondary md:text-3xl">
                    Design decisions stay tied to delivery reality, so the final experience survives construction.
                  </p>
                </div>
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center gap-3 rounded-full bg-accent px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-secondary transition-all duration-300 hover:bg-gold-light hover:text-primary"
                >
                  See the Approach
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
